import React, { createContext, ReactNode, useCallback, useContext, useState, useMemo } from 'react'
import {
  Attachment,
  AttachmentData,
  AttachmentDataWithRequiredAttachment,
  AttachmentStatus,
} from '../../../../../../../@types/types'
import MultimediaDetailsModal from '../multimediaDetailsModal/modal/MultimediaDetailsModal'
import { useOnAttachmentAdded } from './useOnAttachmentAdded'
import { isWebView } from '../../../../../../../utils/navigation'
import WebViewGalleryModal from '../multimediaDetailsModal/modal/webView/WebViewGalleryModal'
import {
  handleTitleChange as handleTitleChangeUtil,
  handleFloorPlanStatusChange as handleFloorPlanStatusChangeUtil,
  onAttachmentDeleteWithHandlingIndex as onAttachmentDeleteWithHandlingIndexUtil,
  createNavigationFunctions as createNavigationFunctionsUtil,
} from './multimediaModalsUtils'

type MultimediaDetailsModalContextProps = {
  openModalForAttachmentIndex: (index: number) => void
  openModalForEditClick: (index: number) => void
  openDetailsModalForSelectedAttachments: (
    selectedAttachments: AttachmentDataWithRequiredAttachment[],
    fromPreview?: boolean
  ) => void
  closeDetailsModal: () => void
  isDetailsModalOpen: boolean
}

export const MultimediaDetailsModalContext = createContext<MultimediaDetailsModalContextProps>(null!)

export function useMultimediaDetailsModalContext() {
  return useContext(MultimediaDetailsModalContext)
}

type Props = {
  attachmentsData: AttachmentData[]
  onAttachmentDelete: (attachment: Attachment) => void
  onAttachmentTitleChange: (attachment: AttachmentDataWithRequiredAttachment) => void
  onAttachmentFloorPlanStatusChange: (attachment: AttachmentDataWithRequiredAttachment) => void
  children?: ReactNode
}

export const MultimediaModalsContextProvider = ({
  attachmentsData,
  onAttachmentDelete,
  onAttachmentTitleChange,
  onAttachmentFloorPlanStatusChange,
  children,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [primaryModal, setPrimaryModal] = useState(false)
  const [secondaryModal, setSecondaryModal] = useState(false)
  const [selectedAttachmentsForEdit, setSelectedAttachmentsForEdit] = useState<AttachmentDataWithRequiredAttachment[]>(
    []
  )
  const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0)
  const [openedFromPreview, setOpenedFromPreview] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const isMobile = isWebView()

  const filteredAttachmentsData = useMemo(() => {
    if (isMobile) {
      return attachmentsData.filter(
        (it) =>
          (it.state === AttachmentStatus.PERSISTED || it.state === AttachmentStatus.UPLOADING) &&
          (it.attachment || it.file) // For uploading attachments, file exists instead of attachment
      )
    } else {
      return attachmentsData.filter((it) => it.state === AttachmentStatus.PERSISTED && it.attachment)
    }
  }, [attachmentsData, isMobile])

  const attachmentsThatOpenModal = useMemo(
    () => filteredAttachmentsData.filter((attachment) => attachment.openDetailsModalOnAdd === true),
    [filteredAttachmentsData]
  )

  const closePrimaryModal = useCallback(() => {
    setPrimaryModal(false)
    setIsEditMode(false)
  }, [])

  const closeSecondaryModal = useCallback(() => {
    setSelectedAttachmentsForEdit([])
    setCurrentAttachmentIndex(0)
    setSecondaryModal(false)
    setOpenedFromPreview(false)
    if (isMobile && !openedFromPreview) {
      setPrimaryModal(true)
    }
  }, [isMobile, openedFromPreview])

  const openModalForAttachmentIndex = useCallback((index: number) => {
    setCurrentIndex(index)
    setPrimaryModal(true)
  }, [])

  const openModalForEditClick = useCallback((index: number) => {
    setCurrentIndex(index)
    setPrimaryModal(true)
    setIsEditMode(true)
  }, [])

  const openDetailsModalForSelectedAttachments = useCallback(
    (selectedAttachments: AttachmentDataWithRequiredAttachment[], fromPreview = false) => {
      setSelectedAttachmentsForEdit(selectedAttachments)
      setCurrentAttachmentIndex(0)
      setOpenedFromPreview(fromPreview)
      setSecondaryModal(true)
      setPrimaryModal(false)
    },
    []
  )

  const openModalAfterAttachmentUpload = useCallback(
    (index: number) => {
      if (!primaryModal) {
        const attachmentToOpen = attachmentsThatOpenModal[index]
        const attachmentIdentifier = attachmentToOpen?.attachment?.id ?? attachmentToOpen?.file?.id
        const modalIndex = filteredAttachmentsData.findIndex(
          (attachment) => (attachment.attachment?.id ?? attachment.file?.id) === attachmentIdentifier
        )

        if (modalIndex !== -1) {
          setCurrentIndex(modalIndex)
          setPrimaryModal(true)
        }
      }
    },
    [attachmentsThatOpenModal, filteredAttachmentsData, primaryModal]
  )

  // Unified navigation logic for both modals
  const createNavigationFunctions = useCallback(createNavigationFunctionsUtil, [])

  // Primary modal navigation
  const primaryModalNav = createNavigationFunctions(currentIndex, setCurrentIndex, attachmentsData.length)
  const showButtonPreviousImage = primaryModalNav.showPrevious
  const showButtonNextImage = primaryModalNav.showNext
  const prevAttachment = primaryModalNav.goPrevious
  const nextAttachment = primaryModalNav.goNext

  // Secondary modal navigation
  const secondaryModalNav = createNavigationFunctions(
    currentAttachmentIndex,
    setCurrentAttachmentIndex,
    selectedAttachmentsForEdit.length
  )
  const handlePreviousImage = secondaryModalNav.goPrevious
  const handleNextImage = secondaryModalNav.goNext

  const handleTitleChange = useCallback(
    (attachment: AttachmentDataWithRequiredAttachment) => {
      handleTitleChangeUtil(attachment, setSelectedAttachmentsForEdit, onAttachmentTitleChange)
    },
    [onAttachmentTitleChange]
  )

  const handleFloorPlanStatusChange = useCallback(
    (attachment: AttachmentDataWithRequiredAttachment) => {
      handleFloorPlanStatusChangeUtil(attachment, setSelectedAttachmentsForEdit, onAttachmentFloorPlanStatusChange)
    },
    [onAttachmentFloorPlanStatusChange]
  )
  const onAttachmentDeleteWithHandlingIndex = useCallback(
    (attachment: Attachment) => {
      onAttachmentDeleteWithHandlingIndexUtil(
        attachment,
        onAttachmentDelete,
        currentIndex,
        attachmentsData,
        setCurrentIndex
      )
    },
    [onAttachmentDelete, attachmentsData, currentIndex, setCurrentIndex]
  )

  useOnAttachmentAdded(attachmentsThatOpenModal, openModalAfterAttachmentUpload)

  // Modal visibility conditions
  const hasAttachments = filteredAttachmentsData.length > 0
  const isDesktop = !isMobile
  const isMobileDevice = isMobile

  const shouldShowDesktopDetailsModal = hasAttachments && isDesktop
  const shouldShowWebViewGalleryModal = hasAttachments && isMobileDevice
  const shouldShowSecondaryDetailsModal = isMobileDevice && secondaryModal && selectedAttachmentsForEdit.length > 0

  function handleDelete() {
    return () => {
      const currentAttachment = filteredAttachmentsData[currentIndex]
      if (currentAttachment?.attachment) {
        onAttachmentDeleteWithHandlingIndex(currentAttachment.attachment)
      }
    }
  }

  return (
    <MultimediaDetailsModalContext.Provider
      value={{
        openModalForAttachmentIndex,
        openModalForEditClick,
        openDetailsModalForSelectedAttachments,
        closeDetailsModal: closeSecondaryModal,
        isDetailsModalOpen: secondaryModal,
      }}
    >
      {children}

      {/* Desktop: Primary Modal */}
      {shouldShowDesktopDetailsModal && (
        <MultimediaDetailsModal
          attachments={filteredAttachmentsData as AttachmentDataWithRequiredAttachment[]}
          attachmentIndex={currentIndex}
          onTitleChange={onAttachmentTitleChange}
          onFloorPlanStatusChange={onAttachmentFloorPlanStatusChange}
          onDelete={handleDelete()}
          isOpen={primaryModal}
          closeModal={closePrimaryModal}
          onPreviousImage={prevAttachment}
          onNextImage={nextAttachment}
          showButtonPreviousImage={showButtonPreviousImage}
          showButtonNextImage={showButtonNextImage}
        />
      )}

      {/* Mobile: Primary Modal */}
      {shouldShowWebViewGalleryModal && (
        <WebViewGalleryModal
          attachments={filteredAttachmentsData}
          isOpen={primaryModal}
          closeModal={closePrimaryModal}
          isEditMode={isEditMode}
        />
      )}

      {/* Mobile: Secondary Modal */}
      {shouldShowSecondaryDetailsModal && (
        <MultimediaDetailsModal
          attachments={selectedAttachmentsForEdit}
          attachmentIndex={currentAttachmentIndex}
          onTitleChange={handleTitleChange}
          onFloorPlanStatusChange={handleFloorPlanStatusChange}
          onDelete={() => {}} // Not needed for web view - delete button not shown
          isOpen={secondaryModal}
          closeModal={closeSecondaryModal}
          onPreviousImage={handlePreviousImage}
          onNextImage={handleNextImage}
          showButtonPreviousImage={secondaryModalNav.showPrevious}
          showButtonNextImage={secondaryModalNav.showNext}
        />
      )}
    </MultimediaDetailsModalContext.Provider>
  )
}
